import React, { useState, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { css, cssMap as unboundCssMap } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Text } from '@atlaskit/primitives/text';
import { Stack } from '@atlaskit/primitives/stack';
import { Inline } from '@atlaskit/primitives/inline';
import { Flex } from '@atlaskit/primitives/components/flex';
import { RovoIcon } from '@atlaskit/logo';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { GreySwirl } from '../../assets/greySwirl';
import { RovoTelepointer } from '../../assets/telepointerRovo';
import { CarouselModal } from '../CarouselModal/CarouselModal';
import { useRovoAwarenessHandlers } from './handlers';

const i18n = defineMessages({
	bannerHeader: {
		id: 'confluence-home-rovo-awareness.banner.header',
		defaultMessage:
			'Ask Rovo to get you up to speed, create automations, brainstorm ideas, and more.',
		description:
			'Header text displayed in the Rovo awareness banner on Confluence home page. Used to encourage users to try asking Rovo for help with various tasks. "Rovo" is a product name',
	},
	bannerDescription: {
		id: 'confluence-home-rovo-awareness.banner.description',
		defaultMessage:
			'Let your AI teammate do the heavy lifting so you can focus on what matters. Just ask.',
		description: 'Body text appearing the Rovo awareness banner on Confluence home page.',
	},
	bannerAskAnythingCTA: {
		id: 'confluence-home-rovo-awareness.banner.ask-anything-cta',
		defaultMessage: 'Ask Rovo',
		description: 'Text that appears in a primary button',
	},
	bannerMoreIdeasCTA: {
		id: 'confluence-home-rovo-awareness.banner.more-ideas-cta',
		defaultMessage: 'More ideas',
		description: 'Text that appears in a default button',
	},
	searchBarImageText: {
		id: 'confluence-home-rovo-awareness.banner.search-bar-image-text',
		defaultMessage: 'If you Rovo, you know...',
		description: "Text that appears in a decorative search bar that doesn't have any functionality",
	},
	dismissBannerCTA: {
		id: 'confluence-home-rovo-awareness.banner.dismiss-banner-cta',
		defaultMessage: 'Close banner',
		description: 'Text that appears as a label and tooltip for an icon button to close the banner',
	},
});

type RovoAwarenessHomeBannerComponentProps = {
	onDismissed: () => void;
};

export const RovoAwarenessHomeBannerComponent: React.FC<RovoAwarenessHomeBannerComponentProps> = ({
	onDismissed,
}) => {
	const { formatMessage } = useIntl();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (!messageInstanceViewed) {
			return;
		}

		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'RovoAwarenessHomeBanner',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { handleDismiss, handleAskRovoClick, handleMoreIdeasClick } = useRovoAwarenessHandlers({
		fireAnalyticsEvent,
		messageInstanceClicked,
		onDismissed,
		setIsModalOpen,
	});

	return (
		<div css={containerStyles}>
			<Stack xcss={styles.contentStyles} space="space.400">
				<Stack space="space.100">
					<Flex direction="row" alignItems="center" gap="space.100" xcss={styles.relativeStyles}>
						<RovoIcon size="large" appearance="brand" />
						<span css={[unboundStyles.headerStyles, unboundStyles.whiteTextColor]}>
							<FormattedMessage {...i18n.bannerHeader} />
						</span>
					</Flex>
					<Text size="large">
						{/* Can't use Text as it uses ADS tokens -> breaks the design when switching between light/dark themes*/}
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<span css={unboundStyles.whiteTextColor}>
							<FormattedMessage {...i18n.bannerDescription} />
						</span>
					</Text>
				</Stack>
				<Inline space="space.050">
					<button
						css={[
							unboundStyles.buttonStyles,
							unboundStyles.primaryButtonStyles,
							unboundStyles.whiteTextColor,
						]}
						onClick={handleAskRovoClick}
						data-testid="ask-rovo-button"
					>
						<FormattedMessage {...i18n.bannerAskAnythingCTA} />
					</button>
					<button
						css={[unboundStyles.buttonStyles, unboundStyles.defaultButtonStyles]}
						onClick={handleMoreIdeasClick}
					>
						<FormattedMessage {...i18n.bannerMoreIdeasCTA} />
					</button>
				</Inline>
			</Stack>
			<div css={unboundStyles.rightContainerStyles}>
				<div css={unboundStyles.searchBarStyles}>
					{/* The heading here has heading level font sizes, but a weight of 400 -> can't use headings */}
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<span
						css={[
							unboundStyles.headerStyles,
							unboundStyles.rovoTelepointerSpacerStyles,
							unboundStyles.rovoGradientText,
						]}
					>
						<FormattedMessage {...i18n.searchBarImageText} />
					</span>
					<RovoTelepointer />
				</div>
			</div>
			<div css={unboundStyles.dismissIconStyles}>
				<IconButton
					isTooltipDisabled={false}
					label={formatMessage(i18n.dismissBannerCTA)}
					appearance="subtle"
					icon={CrossIcon}
					onClick={handleDismiss}
				/>
			</div>
			<div css={unboundStyles.greySwirlContainerStyles}>
				<div css={unboundStyles.greySwirlStyles}>
					<GreySwirl />
				</div>
			</div>
			<CarouselModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
		</div>
	);
};

const containerStyles = css({
	position: 'relative',
	// We need the color to be hard coded as the design is the same between light and dark themes
	// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
	backgroundColor: '#101214',
	color: token('color.text.inverse'),
	overflow: 'hidden',
	borderRadius: token('radius.xlarge'),
	paddingTop: token('space.500'),
	paddingBottom: token('space.500'),
	paddingLeft: token('space.500'),
	paddingRight: token('space.500'),
	display: 'grid',
	gridTemplateColumns: '1fr',
	border: token('color.border'),
	containerName: 'rovo-awareness-home-banner',
	containerType: 'inline-size',
	'@media (min-width: 64rem)': {
		gridTemplateColumns: '1fr 1fr',
	},
});

/**
 * Every single eslint disable here is based on the design of the banner being the same between dark and light mode. This means
 * we can't use any tokens as they automatically update between the two themes as well as most ADS components as they also use tokens internally.
 * This means we can't use Headings, Texts, and Buttons (all variations of buttons).
 */
const unboundStyles = unboundCssMap({
	rightContainerStyles: {
		position: 'relative',
		overflow: 'hidden',
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'row-reverse',
		zIndex: 1,
		'@container rovo-awareness-home-banner (width < 60rem)': {
			display: 'none',
		},
	},
	headerStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/use-tokens-typography
		fontSize: '24px',
	},
	whiteTextColor: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		color: 'white',
	},
	searchBarPositioner: {
		display: 'flex',
		justifyContent: 'center',
		flexDirection: 'row-reverse',
	},
	searchBarReverse: {
		width: '100%',
		display: 'flex',
	},
	searchBarStyles: {
		overflow: 'hidden',
		borderTopLeftRadius: token('radius.small'),
		borderBottomLeftRadius: token('radius.small'),
		padding: token('space.200'),
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: 'white',
		maxWidth: '360px',
		whiteSpace: 'nowrap',
	},
	rovoTelepointerSpacerStyles: {
		marginRight: token('space.050'),
	},
	greySwirlContainerStyles: {
		position: 'absolute',
		zIndex: '0',
		right: 0,
		height: '100%',
	},
	greySwirlStyles: {
		height: '100%',
	},
	rovoGradientText: {
		backgroundImage:
			'linear-gradient(90deg, rgba(41, 42, 46, 0.70) -9.6%, rgba(31, 105, 218, 0.70) -1.18%, rgba(193, 108, 212, 0.70) 26.1%, rgba(245, 161, 23, 0.70) 69.11%, rgba(121, 174, 68, 0.70) 93.71%, rgba(41, 42, 46, 0.70) 112.12%)',
		backgroundClip: 'text',
		color: 'transparent',
	},
	buttonStyles: {
		// We need the color to be hard coded as the design is the same between light and dark themes
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		color: 'white',
		padding: '6px 12px',
		border: 'none',
		borderRadius: token('radius.medium'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		height: '32px',
		boxSizing: 'border-box',
		cursor: 'pointer',
	},
	primaryButtonStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#0d66e5',
		'&:hover': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#0255cb',
		},
		'&:active': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#144794',
		},
	},
	primaryButtonPressedStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#144794',
	},
	defaultButtonStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		color: 'white',
		border: `1px solid ${token('color.border.accent.gray')}`,
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#091E420F',

		'&:hover': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			color: 'white',
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#A1BDD914',
		},
	},

	dismissIconStyles: {
		top: token('space.150'),
		right: token('space.150'),
		position: 'absolute',
		cursor: 'pointer',
		zIndex: 1,

		'& > button': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			color: 'white',
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#091E420F',
			'&:hover': {
				// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
				backgroundColor: '#A1BDD914',
				// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
				color: 'white',
			},
			'&:focus': {
				// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
				color: 'white',
			},
		},
	},
});

const styles = cssMap({
	contentStyles: {
		minWidth: '576px',
		zIndex: 1,
	},
	headerContainerStyles: {
		overflow: 'hidden',
		height: '100%',
	},
	inlineBlockStyles: {
		display: 'inline-block',
	},
	relativeStyles: {
		position: 'relative',
	},
});
